/* eslint-disable no-use-before-define */
import { gql } from 'graphql-tag';

/**
 * ActivitySchema GraphQL Operations
 *
 * GraphQL queries and mutations for standalone ActivitySchema CRUD operations.
 * These support the Builder.vue component for creating/editing reusable activity templates.
 *
 * @see spec: Builder.vue Standalone ActivitySchema Management
 */

// ============================================================
// Mutations
// ============================================================

/**
 * Create a new ActivitySchema in the library.
 *
 * @requires Authentication - User must be logged in
 * @description Creates a new ActivitySchema document in the collection.
 *              Sets createdBy to the current user and createdAt to current timestamp.
 *              Uses simplified input type that accepts plain JSON for fields, instruction,
 *              and styling without requiring complex envelope/set wrappers.
 * @param data - ActivitySchema fields including name, description, icon, etc.
 * @returns Created ActivitySchema with ID
 */
export const CREATE_ACTIVITY_SCHEMA_MUTATION = gql`
  mutation CreateActivitySchema($data: CreateActivitySchemaInput!) {
    createActivitySchemaSimplified(data: $data) {
      id
      name
      description
      icon
      category
      statusType
      setupRequired
      status
      createdBy
      createdAt
      usageCount
      isSkippable
      isTeamAdk
      isVideoask
      activityImage
      introVideo
    }
  }
`;

/**
 * Update an existing ActivitySchema.
 *
 * @requires Authentication - User must be logged in
 * @requires Permission - Only creator or superadmin can update
 * @description Updates an ActivitySchema with the provided fields.
 *              Sets updatedBy and updatedAt on modification.
 * @param id - The ActivitySchema ID to update
 * @param data - Partial ActivitySchema fields to update
 * @returns Updated ActivitySchema
 */
export const UPDATE_ACTIVITY_SCHEMA_MUTATION = gql`
  mutation UpdateActivitySchema($id: String!, $data: UpdateActivitySchemaInput!) {
    updateActivitySchemaSimplified(id: $id, data: $data) {
      id
      name
      description
      icon
      category
      statusType
      setupRequired
      status
      createdBy
      createdAt
      updatedBy
      updatedAt
      usageCount
      isSkippable
      isTeamAdk
      isVideoask
      activityImage
      introVideo
    }
  }
`;

/**
 * Delete (archive) an ActivitySchema.
 *
 * @requires Authentication - User must be logged in
 * @requires Permission - Only creator or superadmin can delete
 * @description Performs soft delete by setting statusType to 'archived'.
 * @param id - The ActivitySchema ID to delete
 * @returns Boolean success indicator
 */
export const DELETE_ACTIVITY_SCHEMA_MUTATION = gql`
  mutation DeleteActivitySchema($id: String!) {
    deleteActivitySchema(id: $id)
  }
`;

/**
 * Clone an ActivitySchema for embedding in a Program.
 *
 * @requires Authentication - User must be logged in
 * @description Creates a deep copy with new ID, preserving sourceActivitySchemaId for lineage.
 *              Increments usageCount on the source ActivitySchema.
 *              Supports the "Both" save option in SaveDestinationDialog.
 * @param activitySchemaId - The source ActivitySchema ID to clone
 * @param programId - The target Program ID (for reference)
 * @returns CloneActivitySchemaResult with success status and cloned data
 */
export const CLONE_ACTIVITY_SCHEMA_TO_PROGRAM_MUTATION = gql`
  mutation CloneActivitySchemaToProgram($activitySchemaId: String!, $programId: String!) {
    cloneActivitySchemaToProgram(activitySchemaId: $activitySchemaId, programId: $programId) {
      success
      error
      data {
        id
        name
        description
        icon
        category
        statusType
        setupRequired
        status
        sourceActivitySchemaId
        createdBy
        createdAt
        isSkippable
        isTeamAdk
        isVideoask
        activityImage
        introVideo
      }
    }
  }
`;

// ============================================================
// Queries
// ============================================================

/**
 * Query the ActivitySchema library with filters.
 *
 * @requires Authentication - None (public query for browsing)
 * @description Returns paginated, filtered ActivitySchema items for the library modal.
 *              Includes createdByEmail for display purposes.
 *              Ordered by usageCount DESC, createdAt DESC for popularity ranking.
 * @param category - Optional filter by category (Pre, Do, Reflect, etc.)
 * @param statusType - Optional filter by status (draft, published, archived)
 * @param search - Optional search by name (case-insensitive contains)
 * @param skip - Pagination offset (default: 0)
 * @param take - Pagination limit (default: 20)
 * @returns ActivitySchemaLibraryResult with items, totalCount, hasMore
 */
export const GET_ACTIVITY_SCHEMA_LIBRARY_QUERY = gql`
  query GetActivitySchemaLibrary(
    $category: String
    $statusType: String
    $search: String
    $skip: Int
    $take: Int
  ) {
    activitySchemaLibrary(
      category: $category
      statusType: $statusType
      search: $search
      skip: $skip
      take: $take
    ) {
      items {
        id
        name
        category
        icon
        description
        createdBy
        createdByEmail
        createdAt
        statusType
        usageCount
      }
      totalCount
      hasMore
    }
  }
`;

/**
 * Get a single ActivitySchema by ID.
 *
 * @requires Authentication - None (public query)
 * @description Fetches a single ActivitySchema document for editing.
 *              Handles both ObjectId and String ID formats.
 * @param id - The ActivitySchema ID to fetch
 * @returns ActivitySchema or null if not found
 */
export const GET_ACTIVITY_SCHEMA_QUERY = gql`
  query GetActivitySchema($id: String!) {
    activitySchema(where: { id: $id }) {
      id
      name
      description
      icon
      category
      statusType
      setupRequired
      status
      createdBy
      createdAt
      updatedBy
      updatedAt
      usageCount
      sourceActivitySchemaId
      fields
      instruction
      permissions
      buildScope
      presetFields
      styling
      isSkippable
      isTeamAdk
      isVideoask
      activityImage
      introVideo
      employerWebsite
      interviewRequest
      projectScope
      timeToSolveProblem
      required
      optional
      completed
      position
      isPreview
      preview
      builderType
      discordEntityType
      JSONSchema
    }
  }
`;

// ============================================================
// TypeScript Types
// ============================================================

/**
 * Activity Schema Library Item for display
 */
export interface ActivitySchemaLibraryItem {
  id: string;
  name: string;
  category?: string | null;
  icon: string;
  description: string;
  createdBy?: string | null;
  createdByEmail?: string | null;
  createdAt?: string | null;
  statusType?: string | null;
  usageCount?: number | null;
}

/**
 * Activity Schema Library Query Result
 */
export interface ActivitySchemaLibraryResult {
  items: ActivitySchemaLibraryItem[];
  totalCount: number;
  hasMore: boolean;
}

/**
 * Clone Activity Schema Result
 */
export interface CloneActivitySchemaResult {
  success: boolean;
  data?: ActivitySchema | null;
  error?: string | null;
}

/**
 * Full ActivitySchema document (subset of fields commonly used)
 */
export interface ActivitySchema {
  id: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  category?: string | null;
  statusType?: string | null;
  setupRequired?: boolean | null;
  status?: boolean | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  usageCount?: number | null;
  sourceActivitySchemaId?: string | null;
  fields?: unknown;
  instruction?: unknown;
  permissions?: unknown;
  buildScope?: unknown;
  presetFields?: unknown;
  styling?: unknown;
  isSkippable?: boolean | null;
  isTeamAdk?: boolean | null;
  isVideoask?: boolean | null;
  activityImage?: string | null;
  introVideo?: string | null;
  employerWebsite?: string | null;
  interviewRequest?: string | null;
  projectScope?: string | null;
  timeToSolveProblem?: number | null;
  required?: boolean | null;
  optional?: boolean | null;
  completed?: boolean | null;
  position?: number | null;
  isPreview?: boolean | null;
  preview?: boolean | null;
  builderType?: string | null;
  discordEntityType?: string | null;
  JSONSchema?: unknown;
}

/**
 * Input type for creating an ActivitySchema
 */
export interface CreateActivitySchemaInput {
  name: string;
  description: string;
  icon: string;
  setupRequired: boolean;
  status: boolean;
  category?: string;
  statusType?: string;
  fields?: unknown;
  instruction?: unknown;
  permissions?: unknown;
  buildScope?: unknown;
  presetFields?: unknown;
  styling?: unknown;
  isSkippable?: boolean;
  isTeamAdk?: boolean;
  isVideoask?: boolean;
  activityImage?: string;
  introVideo?: string;
  employerWebsite?: string;
  interviewRequest?: string;
  projectScope?: string;
  timeToSolveProblem?: number;
  required?: boolean;
  optional?: boolean;
  completed?: boolean;
  position?: number;
  isPreview?: boolean;
  preview?: boolean;
  builderType?: string;
  discordEntityType?: string;
  JSONSchema?: unknown;
}

/**
 * Input type for updating an ActivitySchema (all fields optional)
 */
export interface UpdateActivitySchemaInput {
  name?: string;
  description?: string;
  icon?: string;
  setupRequired?: boolean;
  status?: boolean;
  category?: string;
  statusType?: string;
  fields?: unknown;
  instruction?: unknown;
  permissions?: unknown;
  buildScope?: unknown;
  presetFields?: unknown;
  styling?: unknown;
  isSkippable?: boolean;
  isTeamAdk?: boolean;
  isVideoask?: boolean;
  activityImage?: string;
  introVideo?: string;
  employerWebsite?: string;
  interviewRequest?: string;
  projectScope?: string;
  timeToSolveProblem?: number;
  required?: boolean;
  optional?: boolean;
  completed?: boolean;
  position?: number;
  isPreview?: boolean;
  preview?: boolean;
  builderType?: string;
  discordEntityType?: string;
  JSONSchema?: unknown;
}

/**
 * Filter input for library queries
 */
export interface ActivitySchemaLibraryFilter {
  category?: string;
  statusType?: string;
  search?: string;
  skip?: number;
  take?: number;
}

/**
 * Standard action result format
 */
export interface ActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}
